import type {
  ThresholdCondition,
  RateCondition,
  SessionRelativeCondition,
  AlertCondition,
} from "../types/alerts";

export interface TimestampedValue {
  value: number;
  timestamp: number;
}

export function evaluateThreshold(
  value: number,
  condition: ThresholdCondition,
): boolean {
  switch (condition.op) {
    case "gt":  return value > condition.value;
    case "lt":  return value < condition.value;
    case "gte": return value >= condition.value;
    case "lte": return value <= condition.value;
  }
}

export function evaluateRate(
  samples: TimestampedValue[],
  condition: RateCondition,
): boolean {
  if (samples.length < 2) return false;
  const first = samples[0];
  const last = samples[samples.length - 1];
  const durationSec = (last.timestamp - first.timestamp) / 1000;
  if (durationSec <= 0) return false;
  const rate = (last.value - first.value) / durationSec;
  switch (condition.op) {
    case "gt": return rate > condition.perSecond;
    case "lt": return rate < condition.perSecond;
  }
}

export function evaluateSessionRelative(
  currentValue: number,
  samples: TimestampedValue[],
  condition: SessionRelativeCondition,
): boolean {
  if (samples.length === 0) return false;
  const sum = samples.reduce((acc, s) => acc + s.value, 0);
  const avg = sum / samples.length;
  if (avg === 0) return false;
  const deviationPercent = ((currentValue - avg) / Math.abs(avg)) * 100;
  switch (condition.op) {
    case "gt": return deviationPercent > condition.percent;
    case "lt": return deviationPercent < -condition.percent;
  }
}

export function evaluateCondition(
  currentValue: number,
  samples: TimestampedValue[],
  condition: AlertCondition,
): boolean {
  switch (condition.type) {
    case "threshold":
      return evaluateThreshold(currentValue, condition);
    case "rate":
      return evaluateRate(samples, condition);
    case "session_relative":
      return evaluateSessionRelative(currentValue, samples, condition);
  }
}
